/**
 * DND (Do-Not-Disturb) workspace integration card.
 *
 * Lets an admin pick a provider (blocklist / MSG91 / Fast2SMS / Textlocal)
 * and manage the per-workspace blocklist. When a provider is set, voice
 * campaigns and manual click-to-call will skip numbers that come back
 * as "on DND" from the provider or match the blocklist.
 *
 * Backend endpoints:
 *   GET/PUT/DELETE /workspaces/{ws_id}/integrations/dnd
 *   GET/POST/DELETE /workspaces/{ws_id}/dnd/blocklist[/{phone10}]
 *   POST   /workspaces/{ws_id}/dnd/check
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Save, Trash2, Plus, X, Search, Upload, FileText, Mail } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const PROVIDERS = [
  { v: "none", label: "Not configured", hint: "No DND check at all — every call goes through." },
  { v: "blocklist", label: "Workspace blocklist only", hint: "Skip only numbers you add here. No external API." },
  { v: "msg91", label: "MSG91 DND Check", hint: "External provider. Needs a key from MSG91 account manager." },
  { v: "fast2sms", label: "Fast2SMS DND lookup", hint: "External provider. Needs an API Authorization Key." },
  { v: "textlocal", label: "Textlocal DND Query", hint: "External provider. Needs an API key from Textlocal panel." },
];

export default function DndCard() {
  const { activeWorkspaceId } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [provider, setProvider] = useState("none");
  const [apiKey, setApiKey] = useState("");
  const [dndUrl, setDndUrl] = useState("");
  const [dndMethod, setDndMethod] = useState("POST");
  const [numberField, setNumberField] = useState("mobile");
  const [responsePath, setResponsePath] = useState("dnd");
  const [truthyValues, setTruthyValues] = useState("true,y,yes,1,dnd");
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [blocklist, setBlocklist] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [newNumbers, setNewNumbers] = useState("");
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPhone, setAuditPhone] = useState("");
  const [digestRunning, setDigestRunning] = useState(false);
  const [newReason, setNewReason] = useState("");

  const load = () => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/integrations/dnd`).then(({ data }) => {
      setCfg(data);
      setProvider(data.provider || "none");
      setDndUrl(data.dnd_url || "");
      setDndMethod(data.dnd_method || "POST");
      setNumberField(data.number_field || "mobile");
      setResponsePath(data.response_path || "dnd");
      setTruthyValues((data.truthy_values || ["true", "y", "yes", "1", "dnd"]).join(","));
    }).catch(() => {});
    api.get(`/workspaces/${activeWorkspaceId}/dnd/blocklist`).then(({ data }) => {
      setBlocklist(data.rows || []);
    }).catch(() => {});
    api.get(`/workspaces/${activeWorkspaceId}/dnd/blocklist/reasons`).then(({ data }) => {
      setReasons(data.rows || []);
    }).catch(() => {});
  };
  useEffect(load, [activeWorkspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/integrations/dnd`, {
        provider,
        api_key: apiKey || undefined,
        dnd_url: dndUrl || undefined,
        dnd_method: dndMethod,
        number_field: numberField,
        response_path: responsePath,
        truthy_values: truthyValues.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("DND settings saved");
      setApiKey("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const removeConfig = async () => {
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/integrations/dnd`);
      toast.success("DND provider removed");
      setConfirmDelete(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  const testCheck = async () => {
    if (!testPhone.trim()) return;
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/dnd/check`, { phone: testPhone.trim() });
      setTestResult(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Check failed")); }
  };

  const addToBlocklist = async () => {
    const phones = newNumbers.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!phones.length) { toast.error("Add at least one number"); return; }
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/dnd/blocklist`, {
        phones, reason: newReason || undefined,
      });
      toast.success(`Added ${data.added}, updated ${data.updated}${data.invalid ? `, ${data.invalid} invalid` : ""}`);
      setNewNumbers(""); setNewReason("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };

  const removeOne = async (phone10) => {
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/dnd/blocklist/${phone10}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  /**
   * CSV upload — accepts any .csv/.txt where phone numbers appear anywhere
   * (one per line, or any column). Splits on commas and newlines, trims
   * quotes, and lets the backend's phone normalizer do the final validation.
   */
  const handleCsvFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("CSV too large — max 5 MB");
      return;
    }
    try {
      const text = await file.text();
      // Grab anything that looks like a phone-ish token: digits, +, -, (, ), space.
      // This is forgiving on messy legal lists (they often have "+91 9876543210"
      // or "9876543210,John Doe,..."). The server rejects anything that
      // doesn't normalize to a 10-digit Indian mobile.
      const matches = text.match(/[+()\d][()\d\s\-.]{6,}/g) || [];
      const phones = Array.from(new Set(matches.map((s) => s.trim()).filter(Boolean)));
      if (!phones.length) {
        toast.error("No phone-like tokens found in the file");
        return;
      }
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/dnd/blocklist`,
        { phones, reason: `csv:${file.name}` });
      toast.success(
        `${data.added} added · ${data.updated} already present · ${data.invalid} invalid`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "CSV import failed")); }
  };

  const onDrop = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const file = ev.dataTransfer?.files?.[0];
    if (file) handleCsvFile(file);
  };

  const onPickCsv = (ev) => {
    const file = ev.target?.files?.[0];
    if (file) handleCsvFile(file);
    ev.target.value = "";
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (auditPhone.trim()) params.set("phone", auditPhone.trim());
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/dnd/audit?${params.toString()}`);
      setAuditRows(data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load audit log")); }
    finally { setAuditLoading(false); }
  };

  const openAudit = () => {
    setAuditOpen(true);
    loadAudit();
  };

  const runDigestNow = async () => {
    setDigestRunning(true);
    try {
      const { data } = await api.post(
        `/cron/dnd-weekly-digest?ws_id=${activeWorkspaceId}`);
      toast.success(
        `Digest sent: ${data.sent} email${data.sent === 1 ? "" : "s"} · ${data.workspaces} workspace`);
    } catch (e) { toast.error(apiErrorMessage(e, "Digest failed")); }
    finally { setDigestRunning(false); }
  };

  const providerMeta = PROVIDERS.find((p) => p.v === provider) || PROVIDERS[0];
  const needsHttp = ["msg91", "fast2sms", "textlocal"].includes(provider);

  return (
    <div className="border border-border p-5 space-y-4" data-testid="dnd-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-base">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            DND (Do-Not-Disturb) check for Indian numbers
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            When enabled, voice campaigns and manual click-to-call skip any number
            that comes back as "on DND" from your provider — or that you've added
            to the workspace blocklist. Skipped jobs are marked <code className="px-1 bg-muted">skipped_dnd</code>.
          </p>
        </div>
        <Badge variant={cfg?.configured && provider !== "none" ? "default" : "secondary"}>
          {cfg?.configured && provider !== "none" ? providerMeta.label : "Off"}
        </Badge>
      </div>

      {/* Compliance-oriented actions. Separated from the Save/Test block
          because they don't depend on the form state. */}
      <div className="flex flex-wrap gap-2 -mt-1">
        <Button
          type="button" size="sm" variant="outline"
          className="h-7 text-[11px] rounded-none"
          onClick={openAudit}
          data-testid="dnd-open-audit"
        >
          <FileText className="w-3 h-3 mr-1" />
          View audit log
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          className="h-7 text-[11px] rounded-none"
          onClick={runDigestNow}
          disabled={digestRunning}
          data-testid="dnd-run-digest"
        >
          <Mail className="w-3 h-3 mr-1" />
          {digestRunning ? "Sending…" : "Send weekly digest now"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Provider</Label>
          <select
            data-testid="dnd-provider-select"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full border border-border bg-background text-sm px-3 py-2 mt-1"
          >
            {PROVIDERS.map((p) => (<option key={p.v} value={p.v}>{p.label}</option>))}
          </select>
          <p className="text-[11px] text-muted-foreground mt-1">{providerMeta.hint}</p>
        </div>
        {needsHttp && (
          <div>
            <Label className="text-xs">
              API key {cfg?.api_key_set && <span className="text-emerald-600">(set — leave blank to keep)</span>}
            </Label>
            <Input
              data-testid="dnd-api-key-input"
              type="password"
              placeholder={cfg?.api_key_set ? "••••••••••••" : "Paste provider API key"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="rounded-none mt-1"
            />
          </div>
        )}
      </div>

      {/* MSG91 — 3-step setup guide. Only shown when the admin picks
          MSG91, and only until they've actually saved an API key. */}
      {provider === "msg91" && !cfg?.api_key_set && (
        <div
          className="border border-blue-500/40 bg-blue-50 text-blue-900 p-3 text-xs space-y-1"
          data-testid="dnd-msg91-setup-guide"
        >
          <div className="font-semibold uppercase tracking-widest text-[10px] text-blue-700">
            Wire MSG91 in 3 steps
          </div>
          <ol className="list-decimal pl-4 space-y-1">
            <li>
              Open{" "}
              <a href="https://control.msg91.com/app/dashboard" target="_blank" rel="noreferrer"
                 className="underline font-semibold" data-testid="dnd-msg91-link-dashboard">
                MSG91 Control Panel
              </a>
              {" "}→ sign in to the account you plan to bill the DND lookups to.
            </li>
            <li>
              Open{" "}
              <a href="https://control.msg91.com/app/user/api" target="_blank" rel="noreferrer"
                 className="underline font-semibold" data-testid="dnd-msg91-link-apikey">
                User → API
              </a>
              {" "}and copy the <b>Auth Key</b> (sometimes called "authkey").
            </li>
            <li>
              Paste it into <b>API key</b> below and hit <b>Save</b>. We check once per number per 24h, so bills stay small.
            </li>
          </ol>
          <p className="text-[11px] text-blue-800/80 pt-1">
            Account manager asks about DND access? Say "DND Check API, same auth key as your transactional SMS."
          </p>
        </div>
      )}

      {needsHttp && (
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-xs underline text-muted-foreground"
          >
            {showAdvanced ? "Hide" : "Show"} endpoint mapping (needed if your provider's shape differs)
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 p-3 bg-muted/40">
              <div className="md:col-span-2">
                <Label className="text-xs">Endpoint URL</Label>
                <Input value={dndUrl} onChange={(e) => setDndUrl(e.target.value)}                  placeholder="https://provider.example/dnd/check"
                  className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs">HTTP method</Label>
                <select value={dndMethod} onChange={(e) => setDndMethod(e.target.value)}
                  className="w-full border border-border bg-background text-sm px-3 py-2 mt-1">
                  <option>POST</option><option>GET</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Number field name in request</Label>
                <Input value={numberField} onChange={(e) => setNumberField(e.target.value)}
                  placeholder="mobile" className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs">Response path to DND flag (dot-separated)</Label>
                <Input value={responsePath} onChange={(e) => setResponsePath(e.target.value)}
                  placeholder="data.dnd" className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs">"On-DND" truthy values (comma-separated)</Label>
                <Input value={truthyValues} onChange={(e) => setTruthyValues(e.target.value)}
                  placeholder="true,y,yes,1,dnd" className="rounded-none mt-1" />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} data-testid="dnd-save-btn">
          <Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save"}
        </Button>
        {cfg?.configured && (
          <Button variant="outline" onClick={() => setConfirmDelete(true)} data-testid="dnd-remove-btn">
            <Trash2 className="w-4 h-4 mr-1" />Remove
          </Button>
        )}
      </div>

      {/* Quick test */}
      <div className="border-t border-border pt-4">
        <Label className="text-xs font-semibold">Test a number</Label>
        <div className="flex gap-2 mt-1">
          <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
            placeholder="+91 98765 43210" className="rounded-none" data-testid="dnd-test-input" />
          <Button variant="outline" onClick={testCheck} data-testid="dnd-test-btn">
            <Search className="w-4 h-4 mr-1" />Check
          </Button>
        </div>
        {testResult && (
          <div className="mt-2 text-xs p-3 bg-muted/40 border border-border" data-testid="dnd-test-result">
            {testResult.on_dnd
              ? <span className="text-red-600 font-semibold">⛔ ON DND</span>
              : <span className="text-emerald-600 font-semibold">✓ Not on DND</span>}
            {" "}
            <span className="text-muted-foreground">
              (source: {testResult.source}{testResult.phone10 ? ` · ${testResult.phone10}` : ""}{testResult.cached ? " · cached" : ""})
            </span>
            {testResult.reason && <div className="mt-1 text-muted-foreground">Reason: {testResult.reason}</div>}
          </div>
        )}
      </div>

      {/* Blocklist */}
      <div className="border-t border-border pt-4">
        <Label className="text-xs font-semibold">Workspace blocklist ({blocklist.length})</Label>
        <p className="text-[11px] text-muted-foreground mb-2">
          Numbers here are always blocked regardless of provider.
        </p>
        {/* Reasons breakdown — tiny audit view so admins can see
            where DND entries are coming from (CSV uploads, customer
            requests, provider sync, etc.). Hidden when the blocklist
            is empty. */}
        {reasons.length > 0 && (
          <div
            className="flex flex-wrap gap-1 mb-2"
            data-testid="dnd-reasons-breakdown"
          >
            {reasons.slice(0, 12).map((r) => (
              <span
                key={r.reason}
                className="chip text-[10px] bg-slate-100 border-slate-300 text-slate-700"
                title={r.last_at ? `Last added ${r.last_at}` : undefined}
                data-testid={`dnd-reason-chip-${r.reason.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                <span className="font-semibold">{r.count}</span>
                {" · "}
                <span className="truncate max-w-[160px] inline-block align-bottom">
                  {r.reason}
                </span>
              </span>
            ))}
            {reasons.length > 12 && (
              <span className="text-[10px] text-muted-foreground self-center">
                +{reasons.length - 12} more
              </span>
            )}
          </div>
        )}
        {/* CSV drop zone — accept any .csv/.txt, parse out phone-like tokens. */}
        <label
          htmlFor="dnd-csv-input"
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={onDrop}
          className="flex items-center gap-2 border-2 border-dashed border-border px-3 py-3 mb-2 cursor-pointer hover:bg-muted/40 transition-colors text-xs"
          data-testid="dnd-csv-drop"
        >
          <Upload className="w-4 h-4 text-muted-foreground" />
          <span>
            <b>Drop a CSV</b> here or click to upload (max 5 MB) — e.g. a no-call list from Legal.
            Numbers in any column, header optional.
          </span>
          <input
            id="dnd-csv-input"
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            onChange={onPickCsv}
            className="hidden"
            data-testid="dnd-csv-input"
          />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <textarea
            data-testid="dnd-add-numbers"
            value={newNumbers}
            onChange={(e) => setNewNumbers(e.target.value)}
            placeholder="One or more Indian mobile numbers, comma or newline separated"
            className="w-full border border-border bg-background text-sm px-3 py-2 h-20"
          />
          <div className="flex flex-col gap-2">
            <Input value={newReason} onChange={(e) => setNewReason(e.target.value)}
              placeholder="Reason (optional)" className="rounded-none" data-testid="dnd-add-reason" />
            <Button variant="outline" onClick={addToBlocklist} className="self-start" data-testid="dnd-add-btn">
              <Plus className="w-4 h-4 mr-1" />Add to blocklist
            </Button>
          </div>
        </div>
        {blocklist.length > 0 ? (
          <div className="max-h-56 overflow-y-auto border border-border divide-y divide-border">
            {blocklist.map((row) => (
              <div key={row.phone10} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                <div>
                  <span className="font-mono">+91 {row.phone10}</span>
                  {row.reason && <span className="text-muted-foreground"> · {row.reason}</span>}
                  {row.created_by_name && <span className="text-muted-foreground"> · by {row.created_by_name}</span>}
                </div>
                <button
                  onClick={() => removeOne(row.phone10)}
                  className="text-red-600 hover:text-red-700"
                  data-testid={`dnd-remove-${row.phone10}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground italic">No numbers on the blocklist yet.</p>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Remove DND configuration?"
          description="Voice campaigns will stop filtering DND numbers (blocklist still applies)."
          onCancel={() => setConfirmDelete(false)}
          onConfirm={removeConfig}
        />
      )}

      {/* Audit log — every provider check (number, provider, decision,
          cached) is persisted to `dnd_audit`. This drawer surfaces the
          latest 200 rows so compliance can answer "did we check number X
          before dialling and what did it say?" without a DB query. */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl p-0" data-testid="dnd-audit-dialog">
          <DialogHeader className="p-4 border-b border-border">
            <DialogTitle className="text-sm font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              DND lookup audit log
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-3">
            <div className="flex gap-2">
              <Input
                data-testid="dnd-audit-filter"
                value={auditPhone}
                onChange={(e) => setAuditPhone(e.target.value)}
                placeholder="Filter by phone (10-digit or substring)"
                className="h-8 text-xs rounded-none flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") loadAudit(); }}
              />
              <Button
                size="sm" variant="outline"
                className="h-8 rounded-none text-xs"
                onClick={loadAudit} disabled={auditLoading}
                data-testid="dnd-audit-refresh"
              >
                <Search className="w-3 h-3 mr-1" />
                {auditLoading ? "Loading…" : "Search"}
              </Button>
            </div>
            {auditRows.length === 0 ? (
              <div className="text-xs text-muted-foreground border border-dashed border-border p-6 text-center">
                {auditLoading ? "Loading…" : "No audit rows yet."}
              </div>
            ) : (
              <div className="border border-border max-h-[60vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-left sticky top-0">
                    <tr>
                      <th className="p-2 font-semibold">When</th>
                      <th className="p-2 font-semibold">Phone</th>
                      <th className="p-2 font-semibold">Decision</th>
                      <th className="p-2 font-semibold">Source</th>
                      <th className="p-2 font-semibold">Cached?</th>
                      <th className="p-2 font-semibold">Context</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.map((r, i) => (
                      <tr key={i} className="border-t border-border" data-testid={`dnd-audit-row-${i}`}>
                        <td className="p-2 tabular-nums text-[11px] text-muted-foreground">
                          {r.checked_at?.slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="p-2 tabular-nums">{r.phone10 || r.raw_phone || "—"}</td>
                        <td className="p-2">
                          <span className={`chip text-[10px] ${r.on_dnd ? "bg-rose-50 border-rose-400 text-rose-700" : "bg-emerald-50 border-emerald-400 text-emerald-700"}`}>
                            {r.on_dnd ? "DND" : "Clear"}
                          </span>
                        </td>
                        <td className="p-2 text-[11px]">{r.source || "—"}</td>
                        <td className="p-2 text-[11px]">{r.cached ? "Yes" : "No"}</td>
                        <td className="p-2 text-[11px] text-muted-foreground truncate max-w-[160px]">{r.context || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
